"use client";

import * as React from "react";
import { ShoppingBag, Star } from "lucide-react";

// ShoppableRail — multi-channel commerce primitive.
//
// Drop this on ANY surface (a stream video/live, a socials post, a search
// result, a podcast episode, a music release) to render askFinz Market products
// that creators have tagged to that content. It fetches the PUBLIC shoppable-tag
// API on shop.askfinz.ai and links each card to the product page, so a viewer
// can buy in-context without leaving the experience.
//
// Renders nothing when the item has no tags, so it is always safe to mount.
//
//   <ShoppableRail surface="stream" refId={video.id} />
//   <ShoppableRail surface="socials" refId={post.id} title="In this post" />

const SHOP_ORIGIN = "https://shop.askfinz.ai";

export type ShoppableSurface = "stream" | "live" | "vod" | "socials" | "search" | "podcast" | "music";

interface ShoppableProduct {
  sku: string;
  title: string;
  price: number;
  was?: number | null;
  currency: string;
  img?: string | null;
  category: string;
  rating: number;
  reviews: number;
  vendor?: { id: string; name: string; flag: string; accent: string } | null;
  url: string;
}

interface ShoppableTag {
  id: string;
  productSku: string;
  seconds?: number | null;
  label?: string | null;
  product: ShoppableProduct | null;
}

export interface ShoppableRailProps {
  surface: ShoppableSurface;
  refId: string;
  title?: string;
  className?: string;
  /** Override the shop origin (defaults to https://shop.askfinz.ai). */
  shopOrigin?: string;
}

const money = (n: number, cur = "€") => cur + Number(n).toLocaleString("en-US");

export function ShoppableRail({ surface, refId, title = "Shop this", className = "", shopOrigin = SHOP_ORIGIN }: ShoppableRailProps) {
  const [tags, setTags] = React.useState<ShoppableTag[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!refId) return;
    let alive = true;
    const url = `${shopOrigin}/api/shop/shoppable?surface=${encodeURIComponent(surface)}&ref=${encodeURIComponent(refId)}`;
    fetch(url, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d) => { if (alive) { setTags((d?.tags || []).filter((t: ShoppableTag) => t.product)); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [surface, refId, shopOrigin]);

  if (!loaded || tags.length === 0) return null;

  return (
    <div className={`my-4 rounded-xl border border-border bg-card p-3 ${className}`}>
      <div className="mb-2 flex items-center gap-2">
        <ShoppingBag size={15} className="text-foreground" />
        <span className="text-sm font-semibold">{title}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">askFinz Market</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {tags.map((t) => {
          const p = t.product!;
          return (
            <a
              key={t.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-40 shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-background transition-shadow hover:shadow-md"
            >
              <div className="relative aspect-[4/3] bg-secondary">
                {p.img ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={p.img} alt="" loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ShoppingBag size={28} /></div>
                )}
                {t.label && <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white">{t.label}</span>}
              </div>
              <div className="flex flex-1 flex-col gap-1 p-2">
                {p.vendor && (
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: p.vendor.accent }} />
                    {p.vendor.name} {p.vendor.flag}
                  </span>
                )}
                <span className="line-clamp-2 text-xs font-medium leading-tight">{p.title}</span>
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Star size={10} className="fill-current" /> {p.rating.toFixed(1)} · {p.reviews.toLocaleString()}
                </span>
                <span className="mt-auto flex items-baseline gap-1 pt-1">
                  <span className="text-sm font-semibold">{money(p.price, p.currency)}</span>
                  {p.was && p.was > p.price && <span className="text-[10px] text-muted-foreground line-through">{money(p.was, p.currency)}</span>}
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
